import { useState } from 'react';
import { requestMicStream } from '../lib/recorder';
import Button from './ui/Button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from './ui/Card';
import Shell from './ui/Shell';

interface PermissionScreenProps {
  onGranted: (stream: MediaStream) => void;
}

type Status = 'idle' | 'requesting' | 'denied' | 'unsupported' | 'error';

export default function PermissionScreen({ onGranted }: PermissionScreenProps) {
  const [status, setStatus] = useState<Status>('idle');
  const [detail, setDetail] = useState<string>('');

  async function handleGrant() {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices) {
      setStatus('unsupported');
      setDetail(
        'Your browser does not expose a microphone API. Please try the latest Chrome, Firefox, Safari, or Edge.',
      );
      return;
    }
    setStatus('requesting');
    setDetail('');
    try {
      const stream = await requestMicStream();
      onGranted(stream);
    } catch (e) {
      const name = e instanceof DOMException ? e.name : '';
      if (
        name === 'NotAllowedError' ||
        name === 'SecurityError' ||
        name === 'PermissionDeniedError'
      ) {
        setStatus('denied');
        setDetail(
          'Microphone access was blocked. Click the camera / lock icon next to the address bar to enable it, then press "Grant microphone access" again.',
        );
      } else if (name === 'NotFoundError' || name === 'OverconstrainedError') {
        setStatus('error');
        setDetail(
          'No microphone was detected. Plug one in (or enable your built-in mic) and try again.',
        );
      } else {
        setStatus('error');
        const msg = e instanceof Error ? e.message : String(e);
        setDetail(`Could not access the microphone: ${msg}`);
      }
    }
  }

  const showError = status === 'denied' || status === 'unsupported' || status === 'error';

  return (
    <Shell>
      <Card>
        <CardHeader>
          <CardTitle>Allow microphone access</CardTitle>
          <p className="text-base text-foreground/80">
            To record your spoken answers we need access to your microphone.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Your browser will show a permission prompt. The microphone is only
            used while you hold the record button.
          </p>
          {showError && (
            <p
              className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
              role="alert"
            >
              {detail}
            </p>
          )}
        </CardContent>
        <CardFooter>
          <Button
            size="lg"
            onClick={handleGrant}
            disabled={status === 'requesting'}
          >
            {status === 'requesting'
              ? 'Waiting for permission…'
              : 'Grant microphone access'}
          </Button>
        </CardFooter>
      </Card>
    </Shell>
  );
}
